import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft, Send } from "lucide-react";
import { useState } from "react";
import { EmptyState } from "@/components/brand/empty-state";
import { ErrorState } from "@/components/brand/error-state";
import { Eyebrow } from "@/components/brand/eyebrow";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  type EditorDestination,
  HeadlineSection,
  PillarsSection,
  PolicySection,
  PROMPTS_KEY,
  SECTIONS,
  type SectionKey,
  SectionNav,
  StructureSection,
  TipSheetSection,
  useBrandConfig,
} from "@/features/brand-templates";
import { InviteSmmDialog } from "@/features/invitations";
import { PromptsSection } from "@/features/prompts";
import {
  type BrandConfigDetail,
  brandConfigsCreateForOrgMutation,
  brandReviewOptions,
  brandReviewQueryKey,
  orgsGetOptions,
} from "@/lib/api";

/**
 * Building a client's brand setup by hand, when no template fitted.
 *
 * Deliberately the same editor as a template: an org's setup and a template are
 * the same shape, so there is one place to learn and one set of endpoints. The
 * difference is what happens at the end. Here the point is to get it good
 * enough to show the client, then invite their manager to review it.
 */
export const Route = createFileRoute("/_authenticated/admin/orgs/$orgId/brand")({
  component: OrgBrandEditorPage,
});

function OrgBrandEditorPage() {
  const { orgId } = Route.useParams();
  const headers = { "X-Org-Id": orgId };

  const org = useQuery(orgsGetOptions({ path: { org_id: orgId } }));
  const review = useQuery({ ...brandReviewOptions({ headers }), meta: { silent: true } });
  const queryClient = useQueryClient();
  // Every org created in the product gets a setup at creation, empty if no
  // template was chosen. One seeded or imported by hand may have none, and
  // that used to be a dead end here: a "reload" with nothing to reload into.
  const start = useMutation({
    ...brandConfigsCreateForOrgMutation(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: brandReviewQueryKey({ headers }) }),
  });

  const configId = review.data?.config?.id;

  if (org.isPending || review.isPending) return <Skeleton className="h-96 rounded-lg" />;
  if (org.isError) return <ErrorState onRetry={() => void org.refetch()} />;

  return (
    <>
      <Button asChild variant="ghost" size="sm" className="mb-4 -ml-2">
        <Link to="/admin/orgs/$orgId" params={{ orgId }}>
          <ArrowLeft /> {org.data.name}
        </Link>
      </Button>

      {configId ? (
        <Editor configId={configId} orgId={orgId} orgName={org.data.name} />
      ) : (
        <EmptyState
          title="No brand setup yet"
          description="Start one here, then fill in how this client sounds and what they talk about. Their manager reviews it once you invite them."
          action={
            <Button
              disabled={start.isPending}
              onClick={() =>
                start.mutate({
                  body: { orgId, name: `${org.data.name} brand setup` },
                })
              }
            >
              {start.isPending ? "Starting" : "Start the brand setup"}
            </Button>
          }
        />
      )}
    </>
  );
}

function Editor({
  configId,
  orgId,
  orgName,
}: {
  configId: string;
  orgId: string;
  orgName: string;
}) {
  const { data: config, isPending, isError, refetch } = useBrandConfig(configId);
  const [active, setActive] = useState<EditorDestination>("voicePillars");
  const [inviting, setInviting] = useState(false);

  if (isPending) return <Skeleton className="h-96 rounded-lg" />;
  if (isError) return <ErrorState onRetry={() => void refetch()} />;

  const filled = SECTIONS.filter((s) => config.progress[s.key]).length;
  // Enough to be worth someone's time to read: how the brand sounds and what it
  // talks about. The rest can follow.
  const readyToShare = config.progress.voicePillars && config.progress.contentPillars;

  return (
    <>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <Eyebrow>{orgName}</Eyebrow>
          <h1 className="mt-1 font-display text-4xl text-destructive md:text-5xl">Brand setup</h1>
          <p className="mt-2 max-w-2xl text-muted-foreground text-sm">
            Built by hand for this client. Fill it in, then invite their manager to review it.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <Badge>{filled} of 6 filled in</Badge>
          <Button onClick={() => setInviting(true)} disabled={!readyToShare}>
            <Send /> Invite their manager
          </Button>
        </div>
      </div>

      {!readyToShare ? (
        <p className="mb-6 rounded-md border border-border bg-muted/40 px-4 py-3 text-muted-foreground text-sm">
          Add at least the voice and content pillars before inviting their manager, so there is
          something worth reviewing when they arrive.
        </p>
      ) : null}

      <div className="grid items-start gap-6 lg:grid-cols-[260px_1fr]">
        <div className="lg:sticky lg:top-6">
          <SectionNav progress={config.progress} active={active} onSelect={setActive} />
        </div>
        <div>
          {active === PROMPTS_KEY ? (
            <PromptsSection configId={config.id} pillars={config.contentPillars} />
          ) : (
            renderSection(active, config)
          )}
        </div>
      </div>

      <InviteSmmDialog open={inviting} onOpenChange={setInviting} orgId={orgId} orgName={orgName} />
    </>
  );
}

function renderSection(key: SectionKey, config: BrandConfigDetail) {
  switch (key) {
    case "voicePillars":
      return <PillarsSection template={config} kind="voice" />;
    case "contentPillars":
      return <PillarsSection template={config} kind="content" />;
    case "headlineFormula":
      return <HeadlineSection template={config} />;
    case "postStructure":
      return <StructureSection template={config} />;
    case "tipSheet":
      return <TipSheetSection template={config} />;
    case "socialPolicy":
      return <PolicySection template={config} />;
  }
}
